import React, { useState, useEffect, useRef, useCallback } from "react";
import "./room.scss";
import "react-toastify/dist/ReactToastify.css";
import { withRouter } from "react-router-dom";

/* REDUX */
import { store } from "../../index";
import { useSelector, useDispatch } from "react-redux";
import {
  pushMessage,
  editMessage,
  likeMessage,
  updateMessage,
  fetchMessagesRequest,
  fetchMessagesSuccess,
  fetchMessagesFailure,
  fetchPageMessagesSuccess,
  fetchPageMessagesFailure,
} from "../../redux/actions";

/* COMPONENTS */
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import Button from "../../components/button/button";
import Input from "../../components/input/input";
import Message from "../../components/message/message";
import Gallery from "../../components/gallery/gallery";
import Placeholder from "../../components/placeholder/placeholder";
import Preloader from "../../components/preloader/preloader";

import AudioNote from "../../assets/notification.mp3";

const Room = ({ match, history }) => {
  const audio = new Audio(AudioNote);

  const dispatch = useDispatch();
  const socket = useSelector((state) => state.socket);
  const room = useSelector((state) => state.room);
  const user = useSelector((state) => state.user);

  const [page, setPage] = useState(0);
  const [preload, setPreload] = useState(false);
  const [lock, setLock] = useState(false);
  const [participants, setParticipants] = useState(0);
  const [message, setMessage] = useState({
    author: {
      authorID: user.data.userID,
      title: user.data.title,
      firstname: user.data.firstname,
      lastname: user.data.lastname,
      tag: user.data.tag,
      picture: user.data.picture ? user.data.picture.url : "",
    },
    message: "",
  });
  const [file, setFile] = useState(false);
  const [upload, setUpload] = useState(false);

  const observer = useRef();
  const lastMessageRef = useCallback(
    (node) => {
      if (preload) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) fetchPageMessages();
      });

      if (node) observer.current.observe(node);
    },
    [preload]
  );

  const broadcastMessage = async (e) => {
    e.preventDefault();
    setUpload(true);

    if (file) {
      const fd = new FormData();
      let fileInfo;

      fd.append("attachment", file);

      // SET AUTH HEADER
      axios.defaults.headers.common["x-auth-token"] = sessionStorage.getItem(
        "kzvb-obleute"
      );

      await axios
        .post(`/api/v1/users/upload`, fd)
        .then((res) => (fileInfo = res.data))
        .catch((err) => {
          setMessage(err.response.data);
        });

      // SET SOCKET EVENT
      const payload = {
        ...message,
        file: fileInfo,
        room: match.params.id,
      };

      socket.emit("message", payload);
      setFile(false);
    } else {
      // SET SOCKET EVENT
      const payload = {
        ...message,
        room: match.params.id,
      };

      socket.emit("message", payload);
    }

    setMessage({ ...message, message: "" });
    setUpload(false);
  };

  const filePreloader = (event) => {
    const file = event.target.files[0];
    const reader = new FileReader();

    // READER EVENTS
    reader.onloadstart = function (event) {
      setUpload(true);
    };

    reader.onload = function (event) {
      setUpload(false);
    };

    reader.readAsText(file);
    setFile(file);
    event.target.value = null;
  };

  const fetchMessages = async () => {
    dispatch(fetchMessagesRequest());
    await axios
      .get(`/api/v1/messages/${match.params.id}`)
      .then((res) => dispatch(fetchMessagesSuccess(res.data)))
      .catch((err) => dispatch(fetchMessagesFailure(err)));
  };

  const fetchPageMessages = async () => {
    const room = store.getState().room;
    if (room.messages.length === room.total) return;

    setPreload(true);
    setPage((prevPage) => prevPage + 50);
    await axios
      .get(`/api/v1/messages/${match.params.id}`, { params: { page } })
      .then((res) => dispatch(fetchPageMessagesSuccess(res.data)))
      .catch((err) => dispatch(fetchPageMessagesFailure(err)))
      .finally(() => setPreload(false));
  };

  const translateRoom = (value) => {
    const payload = room.data.filter((room) => room._id === match.params.id)[0];
    if (!payload) return;
    return payload[value];
  };

  const deleteMessage = async (messageID) => {
    socket.emit("delete", messageID);
  };

  const broadcastLike = async (messageID, userID) => {
    socket.emit("like", { messageID, userID });
  };

  useEffect(() => {
    let mount = true;
    if (mount) {
      // FETCH MESSAGES
      socket.emit("join", match.params.id);
      if (room.loaded) {
        setPage(0);
        fetchMessages();
      }
    }

    return () => (mount = false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [match.params.id]);

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      // CHECK SESSION
      if (!user.data) history.push("/");

      // EVENTS
      socket.on("emitMessage", (payload) => {
        audio.play();
        dispatch(pushMessage(payload));
      });

      socket.on("emitMessageError", (payload) => {
        alert(payload.msg);
      });

      socket.on("emitDelete", (messageID) =>
        dispatch(updateMessage(messageID))
      );

      socket.on("emitEdit", (payload) => dispatch(editMessage(payload)));

      socket.on("emitLike", (payload) => dispatch(likeMessage(payload)));

      socket.on("participants", (payload) => {
        mounted && setParticipants(payload);
      });

      // ERROR HANDLING
      socket.on("reconnect_attempt", () => {
        toast.warning("Verbindung wird wiederaufgebaut");
      });

      socket.on("error", () => {
        toast.error(
          "Sitzung wurde unterbrochen, bitte laden Sie die Seite neu"
        );
      });

      socket.on("disconnect", () => {
        setLock(true);
        setInterval(
          () =>
            toast.error(
              "Ihre Sitzung ist abgelaufen, bitte laden Sie die Seite neu"
            ),
          4000
        );
      });
    }

    // CLEANUP
    return () => {
      mounted = false;
      socket.off("emitMessage");
      socket.off("emitMessageError");
      socket.off("emitDelete");
      socket.off("emitEdit");
      socket.off("participants");
      socket.off("error");
      socket.off("disconnect");
      socket.emit("leave", match.params.id);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <main className='room'>
      <Gallery />
      <ToastContainer limit={8} />
      {room.loaded && room.data ? (
        <React.Fragment>
          <section className='room__info'>
            <h2 className='room__info__title'>
              <span className='icon icon--bubbles-2 mr-3'></span>
              <span>Thema - {translateRoom("title")}</span>
            </h2>
            <p className='room__info__members message--success'>
              <span className='icon icon--earth mr-3'></span>
              <span>Online: {participants}</span>
            </p>
          </section>
          <section className='room__content'>
            {room.messages.length ? (
              <React.Fragment>
                {room.messages.map((msg, idx) =>
                  idx + 1 === room.messages.length ? (
                    <Message
                      key={msg._id}
                      reference={lastMessageRef}
                      msg={msg}
                      text={msg.message}
                      file={msg.file ? msg.file : ""}
                      date={msg.creationDate}
                      state={msg.state}
                      deleteMessage={() => deleteMessage(msg._id)}
                      likeMessage={() =>
                        broadcastLike(msg._id, user.data.userID)
                      }
                    />
                  ) : (
                    <Message
                      key={msg._id}
                      msg={msg}
                      text={msg.message}
                      file={msg.file ? msg.file : ""}
                      date={msg.creationDate}
                      state={msg.state}
                      deleteMessage={() => deleteMessage(msg._id)}
                      likeMessage={() =>
                        broadcastLike(msg._id, user.data.userID)
                      }
                    />
                  )
                )}
                {preload && <Preloader type='inline' />}
              </React.Fragment>
            ) : (
              <Placeholder
                text='Keine Nachrichten vorhanden. Eröffnen Sie das Thema!'
                icon='icon icon--bubbles'
                type='full'
              />
            )}
          </section>
        </React.Fragment>
      ) : (
        <Preloader />
      )}
      <form className='room__footer' onSubmit={(e) => broadcastMessage(e)}>
        {!lock ? (
          <React.Fragment>
            {!upload ? (
              <React.Fragment>
                {file ? (
                  <Input
                    type='text'
                    placeholder='Ihre Nachricht...'
                    value={message.message}
                    file
                    action={() => setFile(false)}
                    onChange={(e) =>
                      setMessage({ ...message, message: e.target.value })
                    }
                  />
                ) : (
                  <Input
                    type='text'
                    placeholder='Ihre Nachricht...'
                    value={message.message}
                    onChange={(e) =>
                      setMessage({ ...message, message: e.target.value })
                    }
                  />
                )}
              </React.Fragment>
            ) : (
              <Input type='text' placeholder='Ihre Nachricht...' disabled />
            )}
            <div className='room__footer__file'>
              <label htmlFor='attachment'>
                <span className='icon icon--files'></span>
              </label>
              <input
                type='file'
                name='attachment'
                id='attachment'
                accept='image/*, .doc,.docx,application/msword, .pdf, .xls, .xlsx'
                onChange={(e) => filePreloader(e)}
              />
            </div>
            {(message.message.length || file) && !upload ? (
              <Button
                type='submit'
                text='Senden'
                className='button-primary button--small'
              />
            ) : message.message.length && upload ? (
              <Button
                type='submit'
                icon='spinner'
                text='upload'
                className='button-primary button--small button--loading'
                disabled
              />
            ) : (
              <Button
                type='submit'
                text='Senden'
                className='button-primary button--small'
                disabled
              />
            )}
          </React.Fragment>
        ) : (
          <Input type='text' placeholder='Ihre Nachricht...' disabled />
        )}
      </form>
    </main>
  );
};

export default withRouter(Room);
